import { useState, useEffect, useCallback } from 'react';
import type { FitPreference, BudgetTier, ConditionOverrides } from '@nfa/types';

export interface SearchHistoryItem {
  id: string;
  query: string;
  fit?: FitPreference;
  budget_tier?: BudgetTier;
  conditions?: ConditionOverrides;
  timestamp: number;
}

const STORAGE_KEY = 'nfa_search_history';
const MAX_HISTORY_ITEMS = 10;

function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

export function useSearchHistory() {
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as SearchHistoryItem[];
        // Sort by timestamp descending (most recent first)
        parsed.sort((a, b) => b.timestamp - a.timestamp);
        setHistory(parsed);
      }
    } catch {
      // Invalid data, reset
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Save history to localStorage whenever it changes
  const saveHistory = useCallback((items: SearchHistoryItem[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Storage full or unavailable
    }
  }, []);

  const addSearch = useCallback((
    query: string,
    fit?: FitPreference,
    budget_tier?: BudgetTier,
    conditions?: ConditionOverrides
  ) => {
    setHistory((prev) => {
      // Check if the same query already exists (ignore fit/conditions for deduplication)
      const existingIndex = prev.findIndex(
        (item) => item.query.toLowerCase() === query.toLowerCase()
      );

      let newHistory: SearchHistoryItem[];

      if (existingIndex !== -1) {
        // Update existing entry and move to top
        const updated = {
          ...prev[existingIndex],
          fit,
          budget_tier,
          conditions,
          timestamp: Date.now(),
        };
        newHistory = [updated, ...prev.filter((_, i) => i !== existingIndex)];
      } else {
        // Add new entry
        const newItem: SearchHistoryItem = {
          id: generateId(),
          query,
          fit,
          budget_tier,
          conditions,
          timestamp: Date.now(),
        };
        newHistory = [newItem, ...prev];
      }

      // Keep only the most recent items
      newHistory = newHistory.slice(0, MAX_HISTORY_ITEMS);
      saveHistory(newHistory);
      return newHistory;
    });
  }, [saveHistory]);

  const removeSearch = useCallback((id: string) => {
    setHistory((prev) => {
      const newHistory = prev.filter((item) => item.id !== id);
      saveHistory(newHistory);
      return newHistory;
    });
  }, [saveHistory]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    history,
    addSearch,
    removeSearch,
    clearHistory,
  };
}
